import type { ReactElement, FC } from 'react';
import React, { Fragment } from 'react';
import type { MessageDescriptor } from 'react-intl';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { css } from '@compiled/react';

import Spinner from '@atlaskit/spinner/spinner';
import { token } from '@atlaskit/tokens';

import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { ViewportObserver } from '@confluence/viewport-observer';

import type { HomeViewFilters } from './homeViewsQueryHelpers.ts';
import { LoadingState } from './homeViewsQueryHelpers.ts';
import { NoResults } from './NoResults.tsx';
import type { EmptyImageSources } from './Empty.tsx';
import { Empty } from './Empty.tsx';
import { Error } from './Error.tsx';
import type { RecentFilter } from './Recent/useRecentPreferences.ts';

const loadingMoreStyles = css({
	marginTop: token('space.400'),
	// Accounts for jumping due to spinner changing heights
	height: '72px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

const loadingOverlayStyles = css({
	pointerEvents: 'none',
	position: 'absolute',
	top: '0',
	right: '0',
	bottom: '0',
	left: '0',
	display: 'flex',
	'-webkit-box-align': 'center',
	alignItems: 'center',
	'-webkit-box-pack': 'center',
	justifyContent: 'center',
	opacity: '0.5',
});

const overlaySpinnerContainerStyles = css({
	position: 'absolute',
	top: '200px',
	left: '50%',
});

type LoadingAndPaginationProps = {
	pageCardDataLength: number;
	loading: LoadingState;
	error?: ApolloErrorInterop;
	hasNextPage: boolean;
	loadMore: () => void;
	noResultsMessage: MessageDescriptor;
	noResultsSubtext?: MessageDescriptor;
	emptyMessageHeader: MessageDescriptor;
	emptyMessageBody: MessageDescriptor;
	emptyMessageImage?: EmptyImageSources;
	emptyMessageCallToAction?: ReactElement;
	filterText: string;
	source: string;
	filter: HomeViewFilters | RecentFilter;
};

export const LoadingAndPagination: FC<LoadingAndPaginationProps> = ({
	pageCardDataLength,
	loading,
	error,
	hasNextPage,
	loadMore,
	noResultsMessage,
	noResultsSubtext,
	emptyMessageHeader,
	emptyMessageBody,
	emptyMessageImage,
	emptyMessageCallToAction,
	filterText,
	source,
	filter,
}) => {
	return (
		<Fragment>
			<div aria-live="assertive" aria-atomic="true">
				{/*empty states*/}
				{loading === LoadingState.done && pageCardDataLength === 0 && !hasNextPage && !error ? (
					filterText !== '' ? (
						<NoResults
							message={noResultsMessage}
							subMessage={noResultsSubtext}
							filterText={filterText}
							source={source}
							filter={filter}
						/>
					) : (
						<Empty
							header={emptyMessageHeader}
							body={emptyMessageBody}
							image={emptyMessageImage}
							callToAction={emptyMessageCallToAction}
						/>
					)
				) : null}

				{/*error states*/}
				{error ? (
					<ErrorDisplay error={error}>
						<Error />
					</ErrorDisplay>
				) : null}
			</div>

			{/* loading states */}
			{loading === LoadingState.initial && (
				<Fragment>
					<div css={loadingOverlayStyles} />
					<div css={overlaySpinnerContainerStyles}>
						<Spinner size="large" testId="view-loading" />
					</div>
				</Fragment>
			)}

			{hasNextPage && (filterText !== '' || pageCardDataLength !== 0) && (
				<Fragment>
					<ViewportObserver onViewportEnter={loadMore}>
						<div
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							style={{ position: 'absolute', bottom: '0', height: '300px' }}
						/>
					</ViewportObserver>
					<div css={loadingMoreStyles}>
						<Spinner size="large" testId="page-loading" />
					</div>
				</Fragment>
			)}
		</Fragment>
	);
};
